import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Dialog, Input, useToast } from "@chotca/ui";
import {
  SOURCING_PACKS,
  BOOST_PACKS,
  nextPlanUp,
  type PlanCode,
  type TopupPack,
} from "@chotca/config";
import {
  useMe,
  usePlanStatus,
  usePlanCatalog,
  useSubscribePlan,
  useBuyTopup,
  usePlanPurchaseQr,
  useConfirmPlanPurchase,
  useRedeemGiftCode,
} from "@chotca/api-client";
import type { PlanPurchaseQrResponse } from "@chotca/config";
import { PostingLimitReachedContent } from "./posting-limit-reached-content";
import { VietQrDialog } from "./vietqr-dialog";
import {
  OrderConfirmDialog,
  type OrderConfirmDetails,
} from "./order-confirm-dialog";

type TopupKind = "SOURCING" | "BOOST";

function fmtVnd(n: number): string {
  return `${n.toLocaleString("vi-VN")}₫`;
}

// Build the OrderConfirmDialog details for a plan (LEGAL-04). Feature lines come
// from the plan catalog so quotas stay in sync with what the user is buying.
function planOrderDetails(p: {
  name: string;
  priceVnd: number;
  sourcingQuota: number;
  maxConcurrentPostings: number;
  boostQuota: number;
}): OrderConfirmDetails {
  return {
    productName: `Gói ${p.name}`,
    featureLines: [
      `${p.maxConcurrentPostings} tin tuyển dụng mở cùng lúc`,
      `${p.sourcingQuota} lượt săn ứng viên / tháng`,
      `${p.boostQuota} lượt đẩy tin / tháng`,
    ],
    amountVnd: p.priceVnd,
    priceSuffix: "/ tháng",
  };
}

// Build the OrderConfirmDialog details for an à-la-carte top-up pack.
function topupOrderDetails(
  kind: TopupKind,
  pack: TopupPack,
): OrderConfirmDetails {
  const unit = kind === "BOOST" ? "lượt đẩy tin" : "lượt săn ứng viên";
  return {
    productName: `Mua thêm ${pack.quantity} ${unit}`,
    featureLines: [`+${pack.quantity} ${unit}`, "Không hết hạn theo kỳ"],
    amountVnd: pack.priceVnd,
  };
}

// Shared "buy/upgrade a plan" flow. Creates the order then, on dev (simulated),
// settles it immediately; on prod, shows the VietQR bank-transfer QR the customer
// scans to pay (the Casso webhook settles the order). Used by both the upgrade
// modal's plan buttons and the direct "Nâng cấp <tier>" CTA.
function useUpgradeFlow(orgId: string, onSettled?: () => void) {
  const subscribe = useSubscribePlan(orgId);
  const qr = usePlanPurchaseQr(orgId);
  const confirm = useConfirmPlanPurchase(orgId);
  const { toast } = useToast();
  const [qrData, setQrData] = useState<PlanPurchaseQrResponse | null>(null);
  const [busy, setBusy] = useState<PlanCode | null>(null);

  async function start(code: PlanCode) {
    if (!orgId) return;
    setBusy(code);
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const res = await subscribe.mutateAsync({
        planCode: code,
        returnUrl: `${origin}/dashboard?plan=ok`,
        cancelUrl: `${origin}/dashboard?plan=cancel`,
        errorUrl: `${origin}/dashboard?plan=error`,
        // LEGAL-04: only reached after the buyer ticked consent in
        // OrderConfirmDialog. The server re-checks this flag and logs the
        // ORDER_CONFIRM consent in the same transaction as the order.
        consentAccepted: true,
      });
      if (res.simulated) {
        // Dev: no bank transfer — settle the order immediately.
        await confirm.mutateAsync(res.purchase.id);
        toast({ tone: "success", title: "Đã nâng cấp gói" });
        setBusy(null);
        onSettled?.();
      } else {
        // Prod: show the VietQR QR; the Casso webhook settles once paid.
        const data = await qr.mutateAsync(res.purchase.orderCode);
        setQrData(data);
        setBusy(null);
      }
    } catch {
      toast({ tone: "error", title: "Không tạo được đơn nâng cấp" });
      setBusy(null);
    }
  }

  function reset() {
    setQrData(null);
    setBusy(null);
  }

  return { start, qrData, busy, reset, onSettled };
}

// ─────────────────────────────────────────────────── Provider / context

// Optional context that turns the plain upgrade modal into a "you hit the
// posting limit" explainer (shown above the plan list). Omit for a normal
// upgrade prompt.
type UpgradeContext = {
  reason: "posting_limit";
  limit: number;
  planName: string;
};

type SubscriptionModalsValue = {
  /** Open the plan upgrade modal (choose Pro / Business). Pass a context to
   *  prepend a reason explainer (e.g. posting-limit reached). */
  openUpgrade: (context?: UpgradeContext) => void;
  /** Directly upgrade to a specific plan — skips the chooser. Dev settles
   *  immediately; prod shows the bank-transfer QR. */
  upgradeTo: (code: PlanCode) => void;
  /** Open the à-la-carte top-up modal (buy extra sourcing / boost). */
  openTopup: (kind: TopupKind) => void;
  /** Open the "nhập mã quà tặng" modal (redeem a plan gift code). */
  openRedeemGiftCode: () => void;
};

const Ctx = createContext<SubscriptionModalsValue | null>(null);

export function useSubscriptionModals() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useSubscriptionModals must be used within SubscriptionModalsProvider",
    );
  }
  return ctx;
}

export function SubscriptionModalsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState<UpgradeContext | null>(
    null,
  );
  const [topupKind, setTopupKind] = useState<TopupKind | null>(null);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const nav = useNavigate();
  const [search] = useSearchParams();

  // Direct "Nâng cấp <tier>" flow (skips the chooser). Runs at provider level so
  // the CTA works from anywhere; its bank-transfer QR renders below.
  const me = useMe();
  const directOrgId = me.data?.memberships[0]?.orgId ?? "";
  const directUpgrade = useUpgradeFlow(directOrgId);
  const catalog = usePlanCatalog(directOrgId);

  // upgradeTo asks to confirm first (it creates a paid order), so a stray tap on
  // "Nâng cấp <tier>" doesn't kick off a payment.
  const [pendingCode, setPendingCode] = useState<PlanCode | null>(null);
  const pendingPlan = pendingCode
    ? catalog.data?.items.find((p) => p.code === pendingCode)
    : undefined;

  // A payment redirect may land on /dashboard?plan=ok|cancel|error — just clean
  // the query so a refresh doesn't re-fire and toast the result.
  const { toast } = useToast();
  const flag = search.get("plan");
  useEffect(() => {
    if (!flag) return;
    toast({
      tone: flag === "ok" ? "success" : "default",
      title:
        flag === "ok"
          ? "Thanh toán thành công"
          : flag === "cancel"
            ? "Đã huỷ thanh toán"
            : "Thanh toán chưa hoàn tất",
    });
    const url = new URL(window.location.href);
    url.searchParams.delete("plan");
    nav(url.pathname + (url.search || ""), { replace: true });
  }, [flag, nav, toast]);

  const value = useMemo<SubscriptionModalsValue>(
    () => ({
      openUpgrade: (context) => {
        setUpgradeContext(context ?? null);
        setUpgradeOpen(true);
      },
      upgradeTo: (code) => setPendingCode(code),
      openTopup: (kind) => setTopupKind(kind),
      openRedeemGiftCode: () => setRedeemOpen(true),
    }),
    [],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <UpgradeModal
        open={upgradeOpen}
        context={upgradeContext}
        onClose={() => {
          setUpgradeOpen(false);
          setUpgradeContext(null);
        }}
      />
      <TopupModal kind={topupKind} onClose={() => setTopupKind(null)} />
      <RedeemGiftCodeModal
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
      />

      {/* LEGAL-04 order confirmation before the direct "Nâng cấp <tier>" CTA
          creates a paid order. Shows the full transaction terms + consent tick. */}
      <OrderConfirmDialog
        open={pendingCode != null}
        order={pendingPlan ? planOrderDetails(pendingPlan) : null}
        loading={directUpgrade.busy != null}
        onClose={() => setPendingCode(null)}
        onConfirm={() => {
          const code = pendingCode;
          setPendingCode(null);
          if (code) directUpgrade.start(code);
        }}
      />

      {/* Bank-transfer QR for the direct "Nâng cấp <tier>" CTA. */}
      <VietQrDialog
        qr={directUpgrade.qrData}
        open={!!directUpgrade.qrData}
        orgId={directOrgId}
        onClose={directUpgrade.reset}
      />
    </Ctx.Provider>
  );
}

// ─────────────────────────────────────────────────── Upgrade (plan) modal

function UpgradeModal({
  open,
  context,
  onClose,
}: {
  open: boolean;
  context: UpgradeContext | null;
  onClose: () => void;
}) {
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId ?? "";
  const plan = usePlanStatus(orgId);
  const catalog = usePlanCatalog(orgId);
  const { start: choose, qrData, busy, reset } = useUpgradeFlow(orgId, onClose);

  // LEGAL-04: picking a plan opens the order-confirmation dialog first; the
  // order is only created after the buyer ticks consent there.
  const [pendingCode, setPendingCode] = useState<PlanCode | null>(null);

  useEffect(() => {
    if (!open) {
      reset();
      setPendingCode(null);
    }
  }, [open]);

  const current = plan.data?.effectiveCode;
  const pendingPlan = pendingCode
    ? catalog.data?.items.find((p) => p.code === pendingCode)
    : undefined;

  return (
    <>
      <Dialog
        open={open && !qrData && pendingCode == null}
        onClose={onClose}
        title="Nâng cấp gói"
      >
        <div className="space-y-3">
          {context?.reason === "posting_limit" ? (
            <div className="rounded-xl bg-mist p-3">
              <PostingLimitReachedContent
                limit={context.limit}
                planName={context.planName}
                nextPlanLabel={nextPlanUp(current ?? "FREE")?.label ?? null}
                // Plan choices render right below — omit the upgrade CTA to
                // avoid redundancy; keep close to return to the postings list.
                onClose={onClose}
              />
            </div>
          ) : null}
          <p className="text-body-sm text-ash">
            Chọn gói để mở khoá tính năng săn ứng viên, đăng nhiều tin và boost.
          </p>
          {catalog.isLoading ? (
            <p className="text-body-sm text-ash">Đang tải gói…</p>
          ) : null}
          {(catalog.data?.items ?? []).map((p) => {
            const code = p.code as PlanCode;
            const hasStrike =
              p.originalPriceVnd != null && p.originalPriceVnd > p.priceVnd;
            return (
              <Card key={code} bordered className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-title-md font-extrabold text-ink">
                      {p.name}
                    </p>
                    <p className="text-body-sm text-ash">
                      {p.sourcingQuota} lượt săn · {p.maxConcurrentPostings} tin
                      · {p.boostQuota} boost / tháng
                    </p>
                  </div>
                  <div className="text-right">
                    {hasStrike ? (
                      <p className="text-body-xs text-ash line-through">
                        {fmtVnd(p.originalPriceVnd!)}
                      </p>
                    ) : null}
                    <p className="font-bold text-ink">{fmtVnd(p.priceVnd)}</p>
                    <p className="text-body-xs text-ash">/ tháng</p>
                  </div>
                </div>
                <Button
                  className="mt-3 w-full"
                  loading={busy === code}
                  disabled={current === code}
                  onClick={() => setPendingCode(code)}
                >
                  {current === code ? "Gói hiện tại" : "Chọn gói này"}
                </Button>
              </Card>
            );
          })}
        </div>
      </Dialog>
      {/* LEGAL-04 confirmation between choosing a plan and creating the order. */}
      <OrderConfirmDialog
        open={pendingCode != null}
        order={pendingPlan ? planOrderDetails(pendingPlan) : null}
        loading={busy != null}
        onClose={() => setPendingCode(null)}
        onConfirm={() => {
          const code = pendingCode;
          setPendingCode(null);
          if (code) choose(code);
        }}
      />
      <VietQrDialog
        qr={qrData}
        open={!!qrData}
        orgId={orgId}
        onClose={onClose}
      />
    </>
  );
}

// ─────────────────────────────────────────────────── Redeem gift code modal

// Redeem a "mã quà tặng" — a free plan grant (e.g. PRO 180 ngày) handed out by
// admin. Unlike upgrade/top-up there's no payment and no LEGAL-04 consent, so
// it's a plain code-entry dialog. On success the plan-status queries refresh
// (via useRedeemGiftCode) and we toast what was granted.
function RedeemGiftCodeModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId ?? "";
  const redeem = useRedeemGiftCode(orgId);
  const { toast } = useToast();
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!open) setCode("");
  }, [open]);

  async function submit() {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed || !orgId) return;
    try {
      const res = await redeem.mutateAsync({ code: trimmed });
      toast({
        tone: "success",
        title: `Đã kích hoạt gói ${res.planCode} ${res.durationDays} ngày`,
      });
      onClose();
    } catch (e) {
      // The API returns a Vietnamese message for the known cases (mã không tồn
      // tại / đã dùng / hết hạn / khác gói); surface it, else a generic fallback.
      const message =
        (e as { body?: { message?: string } })?.body?.message ??
        "Không thể sử dụng mã quà tặng";
      toast({ tone: "error", title: message });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nhập mã quà tặng"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Huỷ
          </Button>
          <Button
            onClick={submit}
            loading={redeem.isPending}
            disabled={!code.trim()}
          >
            Kích hoạt
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-body-sm text-ash">
          Nhập mã quà tặng bạn được cấp để kích hoạt gói. Mã chỉ dùng được một
          lần cho tổ chức này.
        </p>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="CHOTCA-XXXX-XXXX"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
      </div>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────── Top-up modal

function TopupModal({
  kind,
  onClose,
}: {
  kind: TopupKind | null;
  onClose: () => void;
}) {
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId ?? "";
  const buy = useBuyTopup(orgId);
  const qr = usePlanPurchaseQr(orgId);
  const confirm = useConfirmPlanPurchase(orgId);
  const { toast } = useToast();
  const [qrData, setQrData] = useState<PlanPurchaseQrResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // LEGAL-04: picking a pack opens the order-confirmation dialog first.
  const [pendingPack, setPendingPack] = useState<TopupPack | null>(null);

  useEffect(() => {
    if (!kind) {
      setQrData(null);
      setBusy(null);
      setPendingPack(null);
    }
  }, [kind]);

  const packs: TopupPack[] = kind === "BOOST" ? BOOST_PACKS : SOURCING_PACKS;
  const unitLabel = kind === "BOOST" ? "lượt boost" : "lượt săn";

  async function choose(pack: TopupPack) {
    if (!orgId || !kind) return;
    setBusy(pack.code);
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const res = await buy.mutateAsync({
        kind,
        packCode: pack.code,
        returnUrl: `${origin}/dashboard?plan=ok`,
        cancelUrl: `${origin}/dashboard?plan=cancel`,
        errorUrl: `${origin}/dashboard?plan=error`,
        // LEGAL-04: reached only after the consent tick in OrderConfirmDialog.
        consentAccepted: true,
      });
      if (res.simulated) {
        // Dev: no bank transfer — settle right away.
        await confirm.mutateAsync(res.purchase.id);
        toast({ tone: "success", title: "Đã mua thêm lượt" });
        onClose();
      } else {
        // Prod: show the VietQR QR; the Casso webhook settles once paid.
        const data = await qr.mutateAsync(res.purchase.orderCode);
        setQrData(data);
        setBusy(null);
      }
    } catch {
      toast({ tone: "error", title: "Không tạo được đơn mua thêm" });
      setBusy(null);
    }
  }

  return (
    <>
      <Dialog
        open={!!kind && !qrData && pendingPack == null}
        onClose={onClose}
        title={kind === "BOOST" ? "Mua thêm lượt boost" : "Mua thêm lượt săn"}
      >
        <div className="space-y-3">
          <p className="text-body-sm text-ash">
            Lượt mua thêm không hết hạn theo kỳ — dùng sau khi hết lượt trong
            gói.
          </p>
          {packs.map((pack) => (
            <Card key={pack.code} bordered className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-title-md font-extrabold text-ink">
                  +{pack.quantity} {unitLabel}
                </p>
                <p className="font-bold text-ink">{fmtVnd(pack.priceVnd)}</p>
              </div>
              <Button
                className="mt-3 w-full"
                loading={busy === pack.code}
                onClick={() => setPendingPack(pack)}
              >
                Mua ngay
              </Button>
            </Card>
          ))}
        </div>
      </Dialog>
      {/* LEGAL-04 confirmation between choosing a pack and creating the order. */}
      <OrderConfirmDialog
        open={pendingPack != null}
        order={
          pendingPack && kind ? topupOrderDetails(kind, pendingPack) : null
        }
        loading={busy != null}
        onClose={() => setPendingPack(null)}
        onConfirm={() => {
          const pack = pendingPack;
          setPendingPack(null);
          if (pack) choose(pack);
        }}
      />
      <VietQrDialog
        qr={qrData}
        open={!!qrData}
        orgId={orgId}
        onClose={onClose}
      />
    </>
  );
}
